"use client";

import {
  RUNTIME_OFFER_PLACEMENT_HI_MUHURAT_REPORT_BRIDGE,
  RUNTIME_OFFER_PLACEMENT_MUHURAT_REPORT_BRIDGE,
} from "@/lib/offer-placement-runtime-keys.generated";

import type { MuhuratResponse } from "@/lib/api/muhurat";
import type { PlaceResult } from "@ishvaram/ui/hooks";
import { OfferPlacement } from "@/components/OfferPlacement";
import { pageReportBridgeOffer } from "@/components/OfferPlacement.page-bridge";
import { reportOfferCopyFor } from "@/lib/report-offer-copy";
import {
  InauspiciousSlots,
  MuhuratSummaryBar,
  MuhuratTimeline,
  PanchangSummary,
} from "@/components/muhurat";

// The reviewed Devanagari muhurat-report copy (Hindi UI is Devanagari since 2026-10-28).
const HI_MUHURAT_REPORT = reportOfferCopyFor("muhurat_report", "hi");

/**
 * Bundle-size discipline (2026-07-16 trim pass): the result renderers +
 * report bridge only paint AFTER the muhurat API responds, so they load as a
 * dynamic chunk from MuhuratClient (in parallel with the fetch) instead of
 * shipping in the route's initial JS. No layout change — the LoadingCard
 * spinner covers both the fetch and the chunk load (rule 58: never a 0px
 * collapse; the picker/header above stay mounted).
 */
export default function MuhuratResultsSection({
  result,
  selectedDate,
  selectedCity,
  shubhCount,
  ashubhCount,
  neutralCount,
  defaultLanguage,
}: {
  result: MuhuratResponse;
  selectedDate: string;
  selectedCity: PlaceResult | null;
  shubhCount: number;
  ashubhCount: number;
  neutralCount: number;
  defaultLanguage: "en" | "hi";
}) {
  const hi = defaultLanguage === "hi";
  const sourcePath = hi ? "/hi/muhurat" : "/muhurat";
  const bridgePlacement = hi
    ? RUNTIME_OFFER_PLACEMENT_HI_MUHURAT_REPORT_BRIDGE
    : RUNTIME_OFFER_PLACEMENT_MUHURAT_REPORT_BRIDGE;
  return (
    <div className="space-y-6">
      <InauspiciousSlots data={result} />
      <PanchangSummary data={result} language={defaultLanguage} />
      <MuhuratSummaryBar
        shubhCount={shubhCount}
        ashubhCount={ashubhCount}
        neutralCount={neutralCount}
      />
      <MuhuratTimeline
        slots={result.slots}
        selectedDate={selectedDate}
        selectedCity={selectedCity}
        language={defaultLanguage}
      />
      <OfferPlacement
        {...pageReportBridgeOffer({
          source: sourcePath,
          planId: "muhurat_report",
          intent: "timing_muhurat",
          placement: bridgePlacement,
          language: defaultLanguage,
        })}
        headline={hi ? HI_MUHURAT_REPORT.headline : "Get a personal Muhurat & Sankalp Plan"}
        subhead={
          hi
            ? HI_MUHURAT_REPORT.subhead
            : "This calculator shows the city timing layer. The report adds your purpose, kundali, dasha pressure, avoid windows, sankalp, and ritual checklist."
        }
        ctaLabel={hi ? "मुहूर्त रिपोर्ट पाएँ" : "Get Muhurat Report"}
      />
    </div>
  );
}
